<script setup>
import { ref, computed, watch } from 'vue';
import FileMenu from './FileMenu.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { useAuth } from '../../composables/useAuth.js';

/* Eine freie Dokumentengruppe: beliebig viele Einträge mit Bezeichnung und
   Datei. Wird für Verbandspapiere und ähnliche Sammlungen verwendet. */
const props = defineProps({
  player: { type: Object, required: true },
  groupKey: { type: String, required: true },
  title: { type: String, required: true },
  hint: { type: String, default: '' },
  labelPlaceholder: { type: String, default: '' },
});

const { can } = useAuth();
const { upload, remove, busy, error } = useFileActions();
const input = ref(null);
const newLabel = ref('');
/* Dezenter Hinweis, wenn ohne Titel hochgeladen wird — blockiert nichts,
   macht nur sichtbar, dass sonst der Dateiname als Anzeige benutzt wird. */
const showTitleHint = ref(false);

watch(newLabel, (v) => {
  if (v.trim()) showTitleHint.value = false;
});

const docs = computed(() => props.player[props.groupKey] || []);
const canEdit = computed(() => can('documents', 'write'));

function promptUpload() {
  showTitleHint.value = !newLabel.value.trim();
  input.value.click();
}

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  try {
    const stored = await upload(file, `${props.groupKey}/${props.player.id}`);
    if (!props.player[props.groupKey]) props.player[props.groupKey] = [];
    props.player[props.groupKey].push({
      ...stored,
      label: newLabel.value.trim() || file.name,
    });
    newLabel.value = '';
  } catch {
    /* Meldung steht in error */
  }
}

async function onRemove(index) {
  const [gone] = props.player[props.groupKey].splice(index, 1);
  if (gone) await remove(gone);
}
</script>

<template>
  <div class="doc-group">
    <h3>{{ title }}</h3>
    <div v-if="hint" class="hint hint-spaced">{{ hint }}</div>

    <div v-if="!docs.length" class="feed-empty">{{ $t('files.noneUploaded') }}</div>

    <!-- Neueste zuerst, dabei den echten Index mitführen -->
    <div
      v-for="(d, i) in docs.map((doc, idx) => ({ doc, idx })).reverse()"
      :key="i"
      class="uploaded-doc"
    >
      <span class="ud-icon"><i>▪</i></span>
      <div class="ud-body">
        <div class="ud-label">{{ d.doc.label || $t('files.untitled') }}</div>
        <FileMenu :file="d.doc" :name="d.doc.label" area="documents" @remove="onRemove(d.idx)" />
      </div>
    </div>

    <div v-if="canEdit" class="doc-upload-row">
      <div class="doc-upload-field">
        <input
          v-model="newLabel"
          type="text"
          class="doc-label-input"
          :class="{ 'title-empty': showTitleHint }"
          :placeholder="labelPlaceholder || $t('files.labelPlaceholder')"
        />
        <div v-if="showTitleHint" class="title-hint">{{ $t('files.titleMissingHint') }}</div>
      </div>
      <button class="file-label" :disabled="busy" @click="promptUpload">
        {{ busy ? $t('common.loading') : $t('files.uploadFile') }}
      </button>
      <input ref="input" type="file" accept="application/pdf,image/*" hidden @change="onPick" />
    </div>

    <div v-if="error" class="group-error">{{ $t(`errors.${error}`) }}</div>
  </div>
</template>

<style scoped>
.hint-spaced {
  margin-bottom: 10px;
}
.doc-upload-field {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 150px;
}
.doc-label-input.title-empty {
  border-color: var(--amber);
}
.title-hint {
  font-size: var(--fs-2xs);
  color: var(--amber);
  margin-top: 4px;
}
.file-label {
  background: none;
  font: inherit;
}
.file-label:disabled {
  opacity: 0.6;
  cursor: default;
}
.group-error {
  font-size: var(--fs-xs);
  color: var(--red);
  margin-top: 8px;
}
</style>
